// ============================================================
// API Client — Zentraler Fetch-Wrapper
// ============================================================

// In production: API lives on api.booking subdomain
// Locally: Vite proxy handles /api → localhost:8787
function resolveBaseUrl(): string {
	if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
	if (
		typeof window !== "undefined" &&
		window.location.hostname === "booking.as-if-records.com"
	) {
		return "https://api.booking.as-if-records.com/api";
	}
	return "/api";
}

const BASE_URL = resolveBaseUrl();

class ApiError extends Error {
	constructor(
		public status: number,
		public code: string,
		message: string,
	) {
		super(message);
		this.name = "ApiError";
	}
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
	const token = localStorage.getItem("token");

	const res = await fetch(`${BASE_URL}${path}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...options.headers,
		},
	});

	if (res.status === 401) {
		localStorage.removeItem("token");
		window.location.href = "/login";
		throw new ApiError(401, "UNAUTHORIZED", "Nicht autorisiert");
	}

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new ApiError(
			res.status,
			body?.error?.code ?? "UNKNOWN",
			body?.error?.message ?? `HTTP ${res.status}`,
		);
	}

	if (res.status === 204) return undefined as T;
	return res.json();
}

// Convenience methods
export const api = {
	get: <T>(path: string) => request<T>(path),

	post: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "POST",
			body: body ? JSON.stringify(body) : undefined,
		}),

	put: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "PUT",
			body: body ? JSON.stringify(body) : undefined,
		}),

	delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export { ApiError };
